import React from 'react';
import { OrderDestination } from '@hero/schema';
import { OrderDestinationDisplayV2 } from '../Destination/OrderDestinationDisplayV2';
import { cn } from '@hero/web-lib';
import { ArrowDown } from 'lucide-react';
import { LocationIcon, MapPinnedIcon } from '@hero/web-icons';

export interface OrderRouteDisplayV2Props {
  destinations: OrderDestination[];
  showSavedPlace?: boolean;
  showAddress?: boolean;
  showCustomer?: boolean;
  showContact?: boolean;
  className?: string;
}

/**
 * OrderRouteDisplayV2 - Displays a route with multiple destinations connected by dotted lines
 *
 * Uses a timeline layout where the dotted connector line automatically stretches
 * to fill the space between destination icons, regardless of content height.
 *
 * Features:
 * - Circular icon badges for each destination
 * - Auto-stretching dotted lines connecting destinations
 * - Pickup (blue) and Dropoff (green) styling
 * - RTL support via Tailwind rtl: utilities
 * - Configurable display options
 *
 * @example
 * ```tsx
 * <OrderRouteDisplayV2
 *   destinations={order.destinations}
 *   showSavedPlace
 *   showAddress
 *   showCustomer
 * />
 * ```
 */
export const OrderRouteDisplayV2: React.FC<OrderRouteDisplayV2Props> = ({
  destinations,
  showSavedPlace = true,
  showAddress = true,
  showCustomer = true,
  showContact = false,
  className = '',
}) => {
  if (!destinations || destinations.length === 0) {
    return null;
  }

  return (
    <div className={cn('relative', className)}>
      {destinations.map((destination, index) => {
        const isFirst = index === 0;
        const isLast = index === destinations.length - 1;
        const isPickup = destination.type === 'pickup';
        const iconBgColor = isPickup ? 'bg-blue-100' : 'bg-green-100';
        const iconColor = isPickup ? 'text-blue-600' : 'text-green-600';
        const Icon = isPickup ? LocationIcon : MapPinnedIcon;

        return (
          <React.Fragment key={index}>
            <div className={cn('flex gap-3')}>
              {/* Icon column: icon centered, connectors extend above/below */}
              <div className="flex flex-col items-center flex-shrink-0 w-10">
                {/* Top segment: dotted line from previous destination, or empty spacer */}
                {!isFirst ? (
                  <div className="flex-1 w-0 border-s-2 border-dotted border-gray-300" />
                ) : (
                  <div className="flex-1" />
                )}

                {/* Circular Icon Badge (centered by equal flex-1 above and below) */}
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10',
                    iconBgColor,
                  )}>
                  <Icon className={cn('size-5', iconColor)} />
                </div>

                {/* Bottom segment: dotted line to next destination, or empty spacer */}
                {!isLast ? (
                  <div className="flex-1 w-0 border-s-2 border-dotted border-gray-300" />
                ) : (
                  <div className="flex-1" />
                )}
              </div>

              {/* Destination content */}
              <OrderDestinationDisplayV2
                destination={destination}
                showSavedPlace={showSavedPlace}
                showAddress={showAddress}
                showCustomer={showCustomer}
                showContact={showContact}
                hideIcon
                className={'py-6'}
              />
            </div>

            {/* Arrow indicator: zero-height row so it sits exactly at the seam between destinations */}
            {!isLast && (
              <div className={cn('flex h-0 overflow-visible')}>
                <div className="w-10 flex justify-center relative z-10">
                  <div className="bg-white rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-[0_0_0_2px_white] -translate-y-1/2">
                    <ArrowDown className="size-2 text-gray-400" strokeWidth={2} />
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
